import { useState, useEffect, useCallback } from "react";
import { GALLERY_ITEMS } from "../../data/home";
import type { GalleryItem } from "../../types";

export default function GallerySection() {
  const [active, setActive] = useState<GalleryItem | null>(null);

  const close = useCallback(() => setActive(null), []);

  // Close on Escape key
  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [active, close]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [active]);

  return (
    <>
      <section className="bg-ivory py-24 px-5 lg:px-16" id="gallery">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-12">
            <div>
              {/* Section label strip */}
              <div className="flex items-center gap-3 mb-5">
                <span className="font-syne text-[10px] font-bold tracking-[0.28em] uppercase text-ink-20">Past Performance</span>
                <span className="flex-1 h-px bg-ink-10" />
              </div>
              <h2
                className="font-syne font-extrabold text-ink tracking-tight leading-none"
                style={{ fontSize: 'clamp(2.5rem,4vw,4rem)' }}
              >
                The Work Speaks
                <br />
                <em className="font-serif italic font-light text-ink-35" style={{ fontSize: '0.5em' }}>
                  Real events, real results.
                </em>
              </h2>
            </div>
            <a
              href="#contact"
              className="font-syne text-[10px] font-semibold tracking-[0.14em] uppercase text-ink-55 border border-ink-20 px-5 py-2.5 rounded-sm hover:border-violet hover:text-violet transition-all shrink-0"
            >
              Request a Quote →
            </a>
          </div>

          {/* Mosaic grid — 2 col mobile → 3 col lg */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 rounded-xl overflow-hidden">
            {GALLERY_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => setActive(item)}
                className={`relative overflow-hidden bg-fog group cursor-pointer text-left ${
                  item.span === "tall" ? "row-span-2" : ""
                }`}
                style={{ minHeight: item.span === "tall" ? "520px" : "250px" }}
                aria-label={`View full photo: ${item.alt}`}
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />

                {/* Dark gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink/65 group-hover:to-ink/80 transition-all duration-300" />

                {/* Expand hint on hover */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="bg-violet/85 text-white text-[10.5px] font-syne font-medium tracking-[0.15em] uppercase px-4 py-2 rounded-sm">
                    View Full Photo
                  </span>
                </div>

                {/* Tag */}
                <div className="absolute bottom-3.5 left-3.5 z-10">
                  <span className="bg-ivory/90 backdrop-blur text-ink text-[9.5px] font-syne tracking-widest uppercase px-3 py-1.5 rounded-sm">
                    {item.tag}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/90 backdrop-blur-sm p-4"
          onClick={close}
        >
          {/* Stop click-through on the image container */}
          <div
            className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={close}
              className="absolute -top-10 right-0 text-white/60 hover:text-white text-sm tracking-widest uppercase transition-colors flex items-center gap-2"
              aria-label="Close"
            >
              Close <span className="text-base leading-none">✕</span>
            </button>

            {/* Full photo */}
            <img
              src={active.src}
              alt={active.alt}
              className="max-w-full max-h-[80vh] w-auto h-auto object-contain rounded-sm shadow-2xl"
            />

            {/* Caption */}
            <div className="mt-4 flex items-center gap-3">
              <span className="bg-ink/80 text-white/80 text-[11px] font-syne font-medium tracking-[0.12em] uppercase px-4 py-2 rounded-sm">
                {active.tag}
              </span>
              <span className="text-white/40 text-xs">{active.alt}</span>
            </div>
          </div>

          {/* Click outside hint */}
          <p className="absolute bottom-4 left-0 right-0 text-center text-white/25 text-xs tracking-wider">
            Click anywhere outside to close
          </p>
        </div>
      )}
    </>
  );
}
